// src/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from './firebase'; // firebase.jsからauthをインポート
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signOut as firebaseSignOut } from 'firebase/auth';
import { getUserProfile, createUserProfile } from './services/userService';
import { db } from './firebase';
import { doc, updateDoc } from 'firebase/firestore';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // ログイン/新規登録時にユーザープロフィールを取得・なければ作成
        let userProfile = await getUserProfile(firebaseUser.uid);
        // プロフィールが存在しない場合は自動作成
        if (!userProfile) {
          // ベータコード・リファラルコードをlocalStorageから確認
          const isBeta = (() => { try { return localStorage.getItem('nobishiro_beta') === 'true'; } catch { return false; } })();
          const referralCode = (() => { try { return localStorage.getItem('nobishiro_referral') || null; } catch { return null; } })();
          console.log('🎯 新規ユーザー検出 isBeta:', isBeta, 'referral:', referralCode);
          userProfile = await createUserProfile(
            firebaseUser.uid,
            firebaseUser.email,
            firebaseUser.displayName || firebaseUser.email?.split('@')[0]
          );
          // ベータユーザーは自動でBusinessプランに
          if (isBeta) {
            await updateDoc(doc(db, 'users', firebaseUser.uid), {
              subscription: {
                planId: 'business',
                status: 'active',
                isBeta: true,
                betaCode: 'NOBISHIRO2026',
                startDate: new Date().toISOString(),
                endDate: null,
              },
              role: 'user',
              ...(referralCode ? { referredBy: referralCode } : {}),
            });
            userProfile = { ...userProfile, subscription: { planId: 'business', status: 'active', isBeta: true } };
            // ベータフラグをlocalStorageから削除（重複適用防止）
            try { localStorage.removeItem('nobishiro_beta'); localStorage.removeItem('nobishiro_referral'); } catch {}
          }
        }
        setProfile(userProfile);
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: userProfile?.nickname || userProfile?.username || userProfile?.displayName || firebaseUser.displayName,
        });
      } else {
        // ログアウト時
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    // 🎯 デモアカウント用の特別処理
    if (email === 'demo@example.com' && password === 'demo123') {
      console.log('✅ デモアカウントでログイン（開発モード）');
      
      // デモユーザーオブジェクトを作成
      const demoUser = {
        uid: 'demo-user-12345',
        email: 'demo@example.com',
        displayName: 'デモユーザー',
        emailVerified: true
      };
      
      // デモプロフィールを作成
      const demoProfile = {
        id: 'demo-user-12345',
        email: 'demo@example.com',
        username: 'デモユーザー',
        displayName: 'デモユーザー',
        subscription: {
          planId: 'pro',
          status: 'active',
          planName: 'Proプラン'
        },
        role: 'user',
        trizLawsMastery: {},
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };
      
      // ステートを更新
      setUser(demoUser);
      setProfile(demoProfile);
      
      return { user: demoUser };
    }
    
    // 通常のFirebase認証
    return signInWithEmailAndPassword(auth, email, password);
  };
  
  const signOut = () => firebaseSignOut(auth);
  
  // ★★★ 問題があったsignUp関数を修正しました ★★★
  const signUp = async (email, password, displayName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Firebase Authenticationにユーザー名（displayName）を保存します
    await updateProfile(userCredential.user, { displayName });
    // この後、useEffect内のonAuthStateChangedが自動的に走り、プロフィールが作成されるので、ここでは何もする必要はありません。
    return userCredential;
  };

  const value = { 
    user, 
    profile, 
    loading, 
    signIn, 
    signOut, 
    signUp, 
    isAuthenticated: !!user, 
    isAdmin: profile?.role === 'admin' || profile?.role === 'master', 
    isPro: profile?.subscription?.planId === 'pro' || profile?.subscription?.planId === 'business' 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};